import { useState, useRef, useEffect, useCallback } from "react";
import type { FocusEvent, ReactNode } from "react";
import type {
  JournalEntry,
  JournalData,
  JournalVisibility,
  JournalNpcData,
  JournalLocationData,
  JournalQuestData,
  JournalFactionData,
  JournalHandoutData,
  JournalLoreData,
  PageInfo,
  MapNote,
  CharacterSheet,
} from "@roll1/shared";
import { keepObjectiveTicks } from "@roll1/shared";
import { Pin, Eye, Pencil, Check, MoreVertical, MapPin, FileText, Map } from "lucide-react";
import { JOURNAL_TYPE_ICONS } from "../lib/journal-utils";
import { Input } from "./ui/input";
import { MarkdownEditor } from "./ui/markdown-editor";
import { Button } from "./ui/button";
import { VisibilityToggle } from "./ui/visibility-toggle";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import type { TranslationKeys } from "../i18n";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import TagInput from "./TagInput";
import RevealConfirmDialog from "./RevealConfirmDialog";
import NpcForm from "./journal/NpcForm";
import LocationForm from "./journal/LocationForm";
import QuestForm from "./journal/QuestForm";
import FactionForm from "./journal/FactionForm";
import HandoutForm from "./journal/HandoutForm";
import LoreForm from "./journal/LoreForm";
import LinksSection from "./journal/LinksSection";
import JournalEntryView from "./journal/JournalEntryView";
import { useQuestObjectiveTicks } from "../contexts/QuestObjectivesContext";
import { useI18n } from "../i18n";

interface JournalEntryEditorProps {
  entry: JournalEntry;
  entries: JournalEntry[];
  pages: PageInfo[];
  notes?: MapNote[];
  sheets?: CharacterSheet[];
  isGM?: boolean;
  onUpdate: (changes: {
    name?: string;
    tags?: string[];
    data?: Partial<JournalData>;
    visibility?: JournalVisibility;
    noteId?: string;
    characterSheetId?: string;
    revealedFields?: string[];
    group?: string;
  }) => void;
  onReveal: () => void;
  onPin: (pinned: boolean) => void;
  onLink?: (targetId: string, relation: string) => void;
  onUnlink?: (targetId: string, relation: string) => void;
  onNavigateToEntry?: (entryId: string) => void;
  onNavigateToPage?: (pageId: string) => void;
  onOpenSheet?: (sheetId: string) => void;
  /**
   * "prep" (GM Studio beat overview): only the text of a Lore entry
   * (data.content), always editable — no header, tags, visibility or reveal.
   * Saving is the same as in the full editor: edits accumulate in one
   * debounce window, go to THIS entry, flush on unmount, and a server update
   * does not overwrite the field being typed in.
   */
  variant?: "full" | "prep";
  /** "prep": placeholder of the text field. */
  placeholder?: string;
  /** Studio: what the host shows around the entry in view mode (a quest's chain, «Where in the plan»). */
  viewExtras?: {
    top?: ReactNode;
    bottom?: ReactNode;
    studioQuestCard?: boolean;
    /** Studio quest page (mockup quests__quest): the card's status/giver beside the name, and the chain row under it. */
    cardHead?: ReactNode;
    chain?: ReactNode;
  };
  /**
   * Studio page (mockup): «КВЕСТ <name>» as a heading with «Edit» / «Show to players» on its right — no
   * pencil, pin, visibility select or ⋮ in view mode (they are in the edit mode). `eyebrow` is the type word.
   */
  studioPage?: { readonly eyebrow: string };
  /** Open straight in the edit mode (a note just made: the text is what the GM came to write). */
  startEditing?: boolean;
  /** Studio: the record's «⋯» (delete…) at the end of its header line, in view and in edit mode. */
  headerMenu?: ReactNode;
}

const DEBOUNCE_MS = 500;

export default function JournalEntryEditor({
  entry,
  entries,
  pages,
  notes,
  sheets,
  isGM = true,
  onUpdate,
  onReveal,
  onPin,
  onLink,
  onUnlink,
  onNavigateToEntry,
  onNavigateToPage,
  onOpenSheet,
  variant = "full",
  placeholder,
  viewExtras,
  studioPage,
  startEditing = false,
  headerMenu,
}: JournalEntryEditorProps) {
  const { t } = useI18n();
  const ticks = useQuestObjectiveTicks();
  const Icon = JOURNAL_TYPE_ICONS[entry.type];

  // View/Edit mode
  const [isEditing, setIsEditing] = useState(startEditing);
  const [showRevealedFields, setShowRevealedFields] = useState(false);

  // Local state
  const [localName, setLocalName] = useState(entry.name);
  const [localTags, setLocalTags] = useState(entry.tags);
  const [localData, setLocalData] = useState<JournalData>(entry.data);
  const [localVisibility, setLocalVisibility] = useState<JournalVisibility>(
    entry.visibility,
  );
  const [revealDialogOpen, setRevealDialogOpen] = useState(false);
  const [localRevealedFields, setLocalRevealedFields] = useState<string[]>(
    () => entry.revealedFields ?? Object.keys(entry.data),
  );

  // Debounce refs
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const pendingRef = useRef<
    Partial<{
      name: string;
      tags: string[];
      data: Partial<JournalData>;
      visibility: JournalVisibility;
      revealedFields: string[];
    }>
  >({});
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;
  // The entry as the server last had it: what is pending is compared with it before anything is sent.
  const entryRef = useRef(entry);
  entryRef.current = entry;

  // GK: only what really differs from the entry goes out. An editor opened and left (or a field typed in and put
  // back) used to send an update that changed nothing — a write and a broadcast to every GM for no change.
  const sendPending = useCallback(() => {
    const pending = pendingRef.current;
    pendingRef.current = {};
    const cur = entryRef.current;
    const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);
    const out: typeof pending = {};
    if (pending.name !== undefined && pending.name !== cur.name) out.name = pending.name;
    if (pending.tags !== undefined && !same(pending.tags, cur.tags)) out.tags = pending.tags;
    if (pending.visibility !== undefined && pending.visibility !== cur.visibility) out.visibility = pending.visibility;
    if (pending.revealedFields !== undefined && !same(pending.revealedFields, cur.revealedFields ?? Object.keys(cur.data))) {
      out.revealedFields = pending.revealedFields;
    }
    if (pending.data !== undefined) {
      const was = cur.data as unknown as Record<string, unknown>;
      const changed = Object.entries(pending.data as Record<string, unknown>).filter(([k, v]) => !same(v, was[k]));
      if (changed.length > 0) out.data = Object.fromEntries(changed) as Partial<JournalData>;
    }
    if (Object.keys(out).length > 0) onUpdateRef.current(out);
  }, []);

  // Focus tracking
  const focusedFieldRef = useRef<string | null>(null);
  // The focused field was typed in since it got focus. Only then does an
  // incoming update leave it alone: a field merely focused takes the latest
  // text (another editor of the same entry may have just saved into it).
  const typedSinceFocusRef = useRef(false);

  const scheduleSave = useCallback(
    (
      changes: Partial<{
        name: string;
        tags: string[];
        data: Partial<JournalData>;
        visibility: JournalVisibility;
        revealedFields: string[];
      }>,
    ) => {
      // Accumulate everything edited inside one debounce window: a later edit
      // of the same key wins; data is merged key by key (each form sends only
      // the field it changed), so edits of DIFFERENT fields all reach one
      // save. Merging must read the previous pending data before it is
      // replaced — spreading `changes` first overwrote it and the earlier
      // field was lost. Values are taken as-is ('' / false / [] are real).
      const previous = pendingRef.current;
      pendingRef.current = {
        ...previous,
        ...changes,
        ...(changes.data !== undefined
          ? { data: { ...previous.data, ...changes.data } as Partial<JournalData> }
          : {}),
      };
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        debounceRef.current = undefined;
        sendPending();
      }, DEBOUNCE_MS);
    },
    [sendPending],
  );

  // Send what is pending now (same accumulated save, just not waiting).
  const flushPending = useCallback(() => {
    if (!debounceRef.current) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = undefined;
    if (Object.keys(pendingRef.current).length > 0) sendPending();
  }, [sendPending]);

  // Flush on unmount
  useEffect(() => () => flushPending(), [flushPending]);

  // Focus leaves this editor (not a move between its own fields — those stay
  // one save): send what is pending, so another editor of the same entry the
  // GM turns to starts from this text.
  const handleEditorBlur = (e: FocusEvent<HTMLElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) flushPending();
  };
  const handleEditorFocus = () => {
    typedSinceFocusRef.current = false;
  };

  // Sync from server updates (when entry prop changes)
  useEffect(() => {
    const typing = typedSinceFocusRef.current;
    if (!(focusedFieldRef.current === "name" && typing)) setLocalName(entry.name);
    setLocalTags(entry.tags);
    setLocalVisibility(entry.visibility);
    setLocalRevealedFields(entry.revealedFields ?? Object.keys(entry.data));
    // Merge data — skip the field being typed in
    setLocalData((prev) => {
      if (focusedFieldRef.current && focusedFieldRef.current !== "name" && typing) {
        const merged = { ...entry.data } as unknown as Record<string, unknown>;
        const prevRecord = prev as unknown as Record<string, unknown>;
        // Keep focused field's local value
        const focusField = focusedFieldRef.current.split(".")[0];
        if (focusField in prevRecord) {
          merged[focusField] = prevRecord[focusField];
        }
        // A quest objective being typed in keeps its text, but every tick is
        // the server's — it changes only by its own command.
        if (focusField === "objectives" && Array.isArray(merged.objectives)) {
          merged.objectives = keepObjectiveTicks(
            (entry.data as JournalQuestData).objectives ?? [],
            merged.objectives as JournalQuestData["objectives"],
          );
        }
        return merged as unknown as JournalData;
      }
      return entry.data;
    });
  }, [entry.name, entry.tags, entry.data, entry.visibility]);

  // Handlers
  const handleNameChange = (newName: string) => {
    typedSinceFocusRef.current = true;
    setLocalName(newName);
    scheduleSave({ name: newName });
  };

  const handleTagsChange = (newTags: string[]) => {
    typedSinceFocusRef.current = true;
    setLocalTags(newTags);
    scheduleSave({ tags: newTags });
  };

  const handleDataChange = useCallback(
    (partial: Partial<JournalData>) => {
      typedSinceFocusRef.current = true;
      setLocalData((prev) => ({ ...prev, ...partial }) as JournalData);
      scheduleSave({ data: partial });
    },
    [scheduleSave],
  );

  if (variant === "prep" && entry.type === "lore") {
    return (
      <MarkdownEditor
        // F-7 (owner): the prep field takes the free height, not less than 160 px. The editor block (toolbar + field)
        // takes the whole width of its column (owner 30.10: a 75ch field was half the width of its toolbar).
        className="min-h-[160px] flex-1 bg-panel-2/60 text-ui text-fg"
        data-testid="beat-prep-text"
        value={(localData as JournalLoreData).content}
        placeholder={placeholder}
        onValueChange={content => handleDataChange({ content })}
        onFocus={() => {
          focusedFieldRef.current = "content";
          handleEditorFocus();
        }}
        onBlur={(e) => {
          focusedFieldRef.current = null;
          handleEditorBlur(e);
        }}
      />
    );
  }

  const handleVisibilityChange = (v: JournalVisibility) => {
    const previousVisibility = localVisibility;
    setLocalVisibility(v);
    scheduleSave({ visibility: v });
    // When switching gm → all, default all fields to visible
    if (previousVisibility === "gm" && v === "all") {
      const allKeys = Object.keys(localData);
      setLocalRevealedFields(allKeys);
      scheduleSave({ revealedFields: allKeys });
    }
  };

  // The eye hides at once (one click, nothing to type): not after the autosave pause.
  const hideNow = () => {
    handleVisibilityChange("gm");
    flushPending();
  };

  const renderForm = () => {
    switch (entry.type) {
      case "npc":
        return (
          <NpcForm
            data={localData as JournalNpcData}
            onChange={handleDataChange}
            focusedFieldRef={focusedFieldRef}
            entries={entries}
            sheets={sheets}
            characterSheetId={entry.characterSheetId}
            onChangeCharacterSheetId={(sheetId) =>
              onUpdate({ characterSheetId: sheetId ?? "" })
            }
            onOpenSheet={onOpenSheet}
          />
        );
      case "location":
        return (
          <LocationForm
            data={localData as JournalLocationData}
            onChange={handleDataChange}
            focusedFieldRef={focusedFieldRef}
            entries={entries}
            pages={pages}
            onNavigateToPage={onNavigateToPage}
          />
        );
      case "quest":
        return (
          <QuestForm
            data={localData as JournalQuestData}
            onChange={handleDataChange}
            focusedFieldRef={focusedFieldRef}
            entries={entries}
            entryId={entry.id}
            // The tick is its own command; what is typed goes first, so a
            // just-added objective exists on the server when its tick arrives.
            onSetCompleted={
              isGM && ticks !== null
                ? (objectiveId, completed) => {
                    flushPending();
                    ticks.set(entry.id, objectiveId, completed);
                  }
                : undefined
            }
            isCompletedPending={(objectiveId) => ticks?.pending(entry.id, objectiveId) ?? false}
          />
        );
      case "faction":
        return (
          <FactionForm
            data={localData as JournalFactionData}
            onChange={handleDataChange}
            focusedFieldRef={focusedFieldRef}
          />
        );
      case "handout":
        return (
          <HandoutForm
            data={localData as JournalHandoutData}
            onChange={handleDataChange}
            focusedFieldRef={focusedFieldRef}
          />
        );
      case "lore":
        return (
          <LoreForm
            data={localData as JournalLoreData}
            onChange={handleDataChange}
            focusedFieldRef={focusedFieldRef}
          />
        );
      default:
        return null;
    }
  };

  // Build the virtual entry for view mode (with local state applied)
  const viewEntry: JournalEntry = {
    ...entry,
    name: localName,
    tags: localTags,
    data: localData,
    visibility: localVisibility,
    revealedFields: localRevealedFields,
  };

  return (
    <div className="flex flex-col h-full overflow-hidden" onFocus={handleEditorFocus} onBlur={handleEditorBlur}>
      {/* Studio page heading (mockup): eyebrow + name, the two actions on the right. */}
      {studioPage !== undefined && !isEditing && (
        // The record's header (group T): one 40 px line — «КВЕСТ name», then the eye, «Редактировать» and ⋯.
        <div className="pane-head-min flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-1" data-testid="studio-entry-head" data-pane-head="">
          <span className="text-label font-semibold uppercase tracking-label text-dim" data-testid="studio-entry-eyebrow">{studioPage.eyebrow}</span>
          <h2 className="min-w-0 break-words text-h1 font-semibold leading-tight text-fg">{localName || t("journal.editor.namePlaceholder")}</h2>
          {localTags.length > 0 && (
            <span className="flex flex-wrap gap-1">
              {localTags.map((tag) => (
                <Badge key={tag} variant="secondary">{tag}</Badge>
              ))}
            </span>
          )}
          {isGM && (
            <span className="ml-auto flex items-center gap-1.5">
              {/* Owner 30.10: one eye that is the state (Eye / EyeOff warn), as in the Journal and the Library. */}
              {localVisibility !== "personal" && (
                <VisibilityToggle
                  size="md"
                  shown={localVisibility === "all"}
                  testid="studio-entry-visibility"
                  onToggle={() => (localVisibility === "all" ? hideNow() : setRevealDialogOpen(true))}
                />
              )}
              <Button variant="outline" size="sm" data-testid="studio-entry-edit" title={t("journal.editor.edit")} onClick={() => setIsEditing(true)}>
                {t("journal.editor.edit")}
              </Button>
              {headerMenu}
            </span>
          )}
        </div>
      )}
      {/* Header */}
      {!(studioPage !== undefined && !isEditing) && (
      <div className="px-3 py-2 border-b border-line space-y-1.5">
        {/* Row 1: Icon + Name + Edit/Done button */}
        <div className="flex items-center gap-2">
          <Icon size={18} className="shrink-0 text-muted-foreground" />
          {isEditing ? (
            <Input
              className="h-8 text-ui font-semibold flex-1"
              value={localName}
              onChange={(e) => handleNameChange(e.target.value)}
              onFocus={() => {
                focusedFieldRef.current = "name";
              }}
              onBlur={() => {
                focusedFieldRef.current = null;
              }}
              maxLength={200}
              placeholder={t("journal.editor.namePlaceholder")}
            />
          ) : (
            <h2 className="text-ui font-semibold text-fg flex-1 truncate">
              {localName || t("journal.editor.namePlaceholder")}
            </h2>
          )}
          {isGM && (
            <Button
              variant="ghost"
              size="sm"
              className="w-7 p-0 shrink-0"
              onClick={() => {
                // Leaving the form sends what is typed (a tick in the view may follow at once).
                if (isEditing) flushPending();
                setIsEditing(!isEditing);
              }}
              title={isEditing ? t("journal.editor.done") : t("journal.editor.edit")}
            >
              {isEditing ? <Check size={14} /> : <Pencil size={14} />}
            </Button>
          )}
          {isGM && headerMenu}
        </div>

        {/* Studio: the entry's group is set here (the table no longer creates groups). */}
        {studioPage !== undefined && isEditing && (
          <div className="flex items-center gap-2 text-meta text-muted-foreground">
            <label htmlFor={`entry-group-${entry.id}`}>{t("journal.editor.group")}</label>
            <Input
              id={`entry-group-${entry.id}`}
              data-testid="journal-entry-group"
              className="h-7 flex-1 text-meta"
              list={`entry-groups-${entry.id}`}
              defaultValue={(entry as { group?: string }).group ?? ""}
              maxLength={100}
              onBlur={(e) => {
                const next = e.target.value.trim();
                if (next !== ((entry as { group?: string }).group ?? "")) onUpdate({ group: next });
              }}
            />
            <datalist id={`entry-groups-${entry.id}`}>
              {[...new Set(entries.map((x) => (x as { group?: string }).group).filter((g): g is string => typeof g === "string" && g !== ""))].map((g) => (
                <option key={g} value={g} />
              ))}
            </datalist>
          </div>
        )}

        {/* Row 2: Tags */}
        <div className="min-h-5">
          {isEditing ? (
            <TagInput
              tags={localTags}
              onChange={handleTagsChange}
              placeholder={t("journal.editor.tagsPlaceholder")}
            />
          ) : (
            localTags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {localTags.map((tag) => (
                  <Badge
                    key={tag}
                    variant="secondary"
                    className="text-label h-5 px-1.5"
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            )
          )}
        </div>

        {/* Row 3: Visibility + Reveal + Pin + Overflow menu */}
        <div className="flex items-center gap-1.5">
          {/* Owner 30.10: the eye is the state and its switch; showing asks first (the select used to widen at once). */}
          {entry.visibility !== "personal" && (
            <VisibilityToggle
              shown={localVisibility === "all"}
              testid="journal-entry-visibility"
              onToggle={() => (localVisibility === "all" ? hideNow() : setRevealDialogOpen(true))}
            />
          )}
          <Button
            variant="ghost"
            size="sm"
            className={`h-7 w-7 p-0 ml-auto ${entry.pinned ? "text-warn" : "text-muted-foreground"}`}
            onClick={() => onPin(!entry.pinned)}
            title={
              entry.pinned
                ? t("journal.editor.unpin")
                : t("journal.editor.pin")
            }
          >
            <Pin size={14} />
          </Button>

          {/* Overflow menu — hidden when no items */}
          {isGM && (
            (notes && notes.length > 0) ||
            (entry.type === "npc" && sheets && sheets.length > 0) ||
            (entry.type === "location" && pages.length > 0) ||
            localVisibility === "all"
          ) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-7 p-0 text-muted-foreground"
                >
                  <MoreVertical size={14} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {notes && notes.length > 0 && (
                  <DropdownMenuItem
                    className="text-meta"
                    onClick={() => {
                      // Switch to edit mode so the MapNote binding is accessible
                      setIsEditing(true);
                    }}
                  >
                    <MapPin size={14} />
                    {t("journal.editor.bindToPin")}
                  </DropdownMenuItem>
                )}
                {entry.type === "npc" && sheets && sheets.length > 0 && (
                  <DropdownMenuItem
                    className="text-meta"
                    onClick={() => {
                      setIsEditing(true);
                    }}
                  >
                    <FileText size={14} />
                    {t("journal.editor.bindToSheet")}
                  </DropdownMenuItem>
                )}
                {entry.type === "location" && pages.length > 0 && (
                  <DropdownMenuItem
                    className="text-meta"
                    onClick={() => {
                      setIsEditing(true);
                    }}
                  >
                    <Map size={14} />
                    {t("journal.editor.bindToPage")}
                  </DropdownMenuItem>
                )}
                {localVisibility === "all" && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-meta"
                      onClick={() => setShowRevealedFields(!showRevealedFields)}
                    >
                      <Eye size={14} />
                      {t("journal.editor.configureFields")}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      )}

      {/* Revealed Fields (toggled from overflow menu when visibility='all') */}
      {showRevealedFields && localVisibility === "all" && (
        <div
          className="px-3 py-1.5 border-b border-line"
          data-testid="revealed-fields-section"
        >
          <p className="text-meta font-semibold text-muted-foreground mb-0.5">
            {t("journal.editor.revealedFields")}
          </p>
          <p className="text-meta text-dim mb-1.5">
            {t("journal.editor.revealedFieldsHint")}
          </p>
          <div className="flex flex-col gap-1">
            {Object.keys(localData)
              // A note's «For the GM» part is never shown to players: it is not a field to tick.
              .filter((key) => !key.endsWith("Id") && !(entry.type === "lore" && key === "gmNotes"))
              .map((key) => {
                const labelKey =
                  `journal.field.${key}` as keyof TranslationKeys;
                const label =
                  entry.type === "lore" && key === "content"
                    ? t("journal.form.lore.content")
                    : t(labelKey) !== labelKey
                    ? t(labelKey)
                    : key.charAt(0).toUpperCase() + key.slice(1);
                const checked = localRevealedFields.includes(key);
                return (
                  <div key={key} className="flex items-center gap-2">
                    <Checkbox
                      id={`rf-${key}`}
                      checked={checked}
                      onCheckedChange={(c) => {
                        const newFields = c
                          ? [...localRevealedFields, key]
                          : localRevealedFields.filter((f) => f !== key);
                        setLocalRevealedFields(newFields);
                        scheduleSave({ revealedFields: newFields });
                      }}
                    />
                    <Label
                      htmlFor={`rf-${key}`}
                      className="text-meta text-fg cursor-pointer"
                    >
                      {label}
                    </Label>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-3 py-2">
        {isEditing ? (
          <>
            {renderForm()}

            {/* MapNote binding dropdown */}
            {notes && notes.length > 0 && (
              <div className="pt-3 border-t border-line mt-3">
                <label className="text-meta text-muted-foreground">
                  {t("journal.map.bindNote")}
                </label>
                <Select
                  value={entry.noteId ?? "__none__"}
                  onValueChange={(v) =>
                    onUpdate({ noteId: v === "__none__" ? "" : v })
                  }
                >
                  <SelectTrigger className="h-8 text-ui mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t("common.none")}</SelectItem>
                    {notes.map((n) => (
                      <SelectItem key={n.id} value={n.id}>
                        {n.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {entry.noteId &&
                  !notes.find((n) => n.id === entry.noteId) && (
                    <p className="text-meta text-warn mt-1">
                      {t("journal.map.noteNotFound")}
                    </p>
                  )}
              </div>
            )}

            {/* Links section (editable) */}
            {onLink && onUnlink && onNavigateToEntry && (
              <LinksSection
                sourceId={entry.id}
                linksFrom={entry.linksFrom ?? []}
                linksTo={entry.linksTo ?? []}
                entries={entries}
                onLink={onLink}
                onUnlink={onUnlink}
                onNavigateToEntry={onNavigateToEntry}
              />
            )}
          </>
        ) : (
          <>
            {viewExtras?.top}
            {viewExtras?.studioQuestCard === true ? (
              <section className="mt-3 rounded-md border border-line bg-panel-2/40" data-testid="studio-quest-card">
                <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 border-b border-line px-3 py-2">
                  <span className="font-semibold text-fg">{localName}</span>
                  {viewExtras.cardHead}
                </div>
                {viewExtras.chain}
                <JournalEntryView
                  entry={viewEntry}
                  entries={entries}
                  isGM={isGM}
                  onNavigateToEntry={onNavigateToEntry}
                  studioQuestCard
                  hideLinks={isGM && onLink !== undefined && onUnlink !== undefined && onNavigateToEntry !== undefined}
                />
              </section>
            ) : (
            <JournalEntryView
              entry={viewEntry}
              entries={entries}
              isGM={isGM}
              onNavigateToEntry={onNavigateToEntry}
              studioQuestCard={false}
              hideLinks={isGM && onLink !== undefined && onUnlink !== undefined && onNavigateToEntry !== undefined}
            />
            )}
            {/* F-4: the GM's links are made and removed where the entry is (Studio passes the handlers). */}
            {/* A Studio page (mockup) keeps «Links» for the edit mode, where it is always shown. */}
            {isGM && studioPage === undefined && onLink && onUnlink && onNavigateToEntry && (
              <div className="mt-3">
                <LinksSection
                  sourceId={entry.id}
                  linksFrom={entry.linksFrom ?? []}
                  linksTo={entry.linksTo ?? []}
                  entries={entries}
                  onLink={onLink}
                  onUnlink={onUnlink}
                  onNavigateToEntry={onNavigateToEntry}
                />
              </div>
            )}
            {viewExtras?.bottom}
          </>
        )}
      </div>

      <RevealConfirmDialog
        open={revealDialogOpen}
        onOpenChange={setRevealDialogOpen}
        entryName={localName}
        onConfirm={() => {
          onReveal();
          setRevealDialogOpen(false);
        }}
      />
    </div>
  );
}
